import pluralize from "pluralize";

import { ServiceSettings, Settings } from "~/entities/siteEntities";

// How far ahead an order type has to be ordered, in the words used everywhere it's shown (store bar, menu intro,
// store cards, time sheet): short ("Order 2–30 days ahead") and long ("Order at least 2 days ahead, and up to 30
// days."). Null when there's nothing to say.
export type LeadTime = { short: string; long: string };

export const leadTimeFor = (
  settings: Settings | undefined,
): LeadTime | null => {
  if (!settings) {
    return null;
  }
  if (settings.allowAsapOrders) {
    return {
      short: "Order now or schedule ahead",
      long: "Order now or schedule ahead.",
    };
  }
  const rules = settings.schedulingRules;
  const minDays = rules?.minDaysFromNow ?? 0;
  const maxDays = rules?.maxDaysFromNow ?? 0;
  const upTo = maxDays > minDays ? `, and up to ${maxDays} days` : "";
  if (minDays >= 1) {
    return {
      short:
        maxDays > minDays
          ? `Order ${minDays}–${maxDays} days ahead`
          : `Order at least ${pluralize("day", minDays, true)} ahead`,
      long: `Order at least ${pluralize("day", minDays, true)} ahead${upTo}.`,
    };
  }
  const minMinutes = rules?.minMinutesFromNow ?? 0;
  if (minMinutes > 0) {
    const notice =
      minMinutes >= 60
        ? pluralize("hour", Math.ceil(minMinutes / 60), true)
        : pluralize("minute", minMinutes, true);
    return {
      short: `Order at least ${notice} ahead`,
      long: `Order at least ${notice} ahead${upTo}.`,
    };
  }
  return null;
};

// The service settings for one order type at a site
export const settingsFor = (
  serviceSettings: ServiceSettings[] | null | undefined,
  orderTypeCode: string | null | undefined,
): Settings | undefined =>
  serviceSettings?.find((x) => x.orderTypeCode === orderTypeCode)?.settings;

// The minimum the items must come to at full price for an order type at a site, or 0 for none
export const minimumOrderFor = (settings: Settings | undefined): number =>
  Math.max(0, settings?.minimumOrderAmount ?? 0);

// The time sheet's note for an order type that books ahead (catering): the rule, where it applies, and when the
// earliest booking is. Null when ASAP is allowed, which keeps the sheet as it is for everyday ordering.
export const leadNoteFor = ({
  settings,
  kindLabel,
  storeName,
  earliestTodayLabel,
}: {
  settings: Settings | undefined;
  // "Catering pickup"
  kindLabel: string;
  storeName: string;
  // The first bookable time, when that's today ("3:30 PM")
  earliestTodayLabel?: string | null;
}): { title: string; body: string; dayCaption: string | null } | null => {
  if (!settings || settings.allowAsapOrders) {
    return null;
  }
  const rules = settings.schedulingRules;
  const minDays = rules?.minDaysFromNow ?? 0;
  const maxDays = rules?.maxDaysFromNow ?? 0;
  const later =
    maxDays > minDays ? ` You can book up to ${maxDays} days ahead.` : "";
  if (minDays >= 1) {
    return {
      title: `Order at least ${pluralize("day", minDays, true)} ahead`,
      body: `${kindLabel} at ${storeName}.${later}`,
      dayCaption:
        minDays === 1
          ? `Today isn't available for ${kindLabel.split(" ")[0].toLowerCase()}.`
          : minDays === 2
            ? `Today and tomorrow aren't available for ${kindLabel.split(" ")[0].toLowerCase()}.`
            : `The next ${minDays} days aren't available for ${kindLabel.split(" ")[0].toLowerCase()}.`,
    };
  }
  const lead = leadTimeFor(settings);
  if (!lead) {
    return null;
  }
  return {
    title: lead.short,
    body: `${kindLabel} from ${storeName}.${
      earliestTodayLabel ? ` The earliest today is ${earliestTodayLabel}.` : ""
    }`,
    dayCaption: null,
  };
};
